import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { sanitizeProps, useRandomId, useResizeObserver } from '@fs/zion-frontend-friends'
import { Expander, ExpanderContent, InlineButton } from '@fs/zion-ui'

const contentsCss = css`
  /* Remove bottom padding from last paragraph in children so the more/less button will hug a little tighter. */
  & p:last-of-type {
    padding-bottom: 0;
  }
`

const collapsedCss = css`
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: var(--line-clamp);
`

const buttonContainerCss = css`
  margin-top: 4px;
`

function getPx(value) {
  const numStr = value.match(/\d+/)
  return numStr ? parseInt(numStr, 10) : 0 // this 0 is a failsafe for JSDOM testing. Manual testing in browsers proved that numStr is always defined.
}

function getTextContainer(el) {
  let textChild = el
  while (textChild.firstChild && textChild.firstChild.nodeType !== Node.TEXT_NODE) {
    textChild = textChild.firstElementChild
  }
  return textChild
}

export default function DmcMoreLess({
  children,
  collapsedLineCount = 2,
  buttonProps,
  showMore,
  text,
  onMoreLessClick,
  ...props
}) {
  const [open, setOpen] = useState(false)
  const [collapsedHeight, setCollapsedHeight] = useState(0)
  const [clampLines, setClampLines] = useState(true)
  const [expandable, setExpandable] = useState(false)
  const [t] = useTranslation()
  const buttonRef = useRef(null)
  const textRef = useRef(null)
  const expandableTextRef = useRef(null)

  // needed for resizeObserver to update (otherwise, doesn't recognize if a ref changes to a different DOM node)
  const activeRef = useMemo(() => (expandable ? expandableTextRef : textRef), [expandable])

  useEffect(() => {
    setOpen(showMore)
  }, [showMore])

  const onTransitionEnd = useCallback(() => setClampLines(true), [])
  const handleClick = useCallback(() => {
    onMoreLessClick()
    setOpen((o) => !o)
    buttonRef?.current?.blur()
    // When expanding, disable clamping lines
    setClampLines(false)
  }, [onMoreLessClick])

  // Calculate the collapsed height and check if the expander should be used
  const calcExpandable = useCallback(
    (node) => {
      const textChild = getTextContainer(node)
      // When clamped, the clientHeight is only what's visible. Scroll height in that case is the right option.
      const clientHeight = Math.max(textChild.clientHeight, textChild.scrollHeight)
      // if there is no clientHeight (e.g. when hidden in another expander), we cannot calculate whether or not MoreLess is expandable, so any calculations we do below are useless.
      if (!clientHeight) return

      const compStyle = getComputedStyle(textChild)
      const compLineHeight = compStyle.getPropertyValue('line-height')
      let lineHeight = ''

      if (compLineHeight === 'normal') {
        const fontSize = getPx(compStyle.getPropertyValue('font-size'))
        lineHeight = fontSize * 1.2 // this is the value of line-height when it is 'normal', rather than a number
      } else {
        lineHeight = getPx(compLineHeight)
      }

      const height = lineHeight * collapsedLineCount
      const paddingTop = getPx(compStyle.getPropertyValue('padding-top'))
      const paddingBottom = getPx(compStyle.getPropertyValue('padding-bottom'))
      const totalCollapsedHeight = height + paddingTop // No padding bottom because it exposes extra lines when the line-height is close together

      // In firefox the (totalCollapsedHeight + padding) is < the expandedChildHeight, but only by a fraction of a px.
      const canCollapse = clientHeight - (totalCollapsedHeight + paddingBottom) > 1

      setCollapsedHeight(totalCollapsedHeight)
      setExpandable(canCollapse)
    },
    [collapsedLineCount]
  )

  // recalculate expandablility when childrens change and when it resizes
  useLayoutEffect(() => calcExpandable(activeRef.current), [children, calcExpandable, activeRef])
  useResizeObserver(activeRef, () => calcExpandable(activeRef.current))

  const controlsId = useRandomId()

  return (
    <div {...sanitizeProps(props)}>
      {/* We need to not render Expander on first render so MoreLess can calculate the collapsedHeight as quickly as possible. Expander complicates these calculations because it controls height and display logic using the collapsedHeight. */}
      {!expandable ? (
        <div
          ref={textRef}
          className={clampLines ? collapsedCss : contentsCss}
          style={{ '--line-clamp': collapsedLineCount, wordWrap: 'break-word' }}
        >
          {text || children}
        </div>
      ) : (
        <>
          <Expander open={open} setOpen={setOpen} ariaControlsId={controlsId}>
            <ExpanderContent
              collapsedHeight={collapsedHeight}
              // Only give it the transition end handler when it is going to be collapsed (i.e. they just clicked the "Less" button)
              onTransitionEnd={!open ? onTransitionEnd : null}
            >
              <div
                ref={expandableTextRef}
                className={clampLines ? collapsedCss : contentsCss}
                style={{ '--line-clamp': collapsedLineCount, wordWrap: 'break-word' }}
              >
                {children}
              </div>
            </ExpanderContent>
          </Expander>
          <div className={buttonContainerCss}>
            <InlineButton
              {...sanitizeProps(buttonProps)}
              onClick={handleClick}
              aria-hidden
              ref={buttonRef}
              aria-controls={controlsId}
            >
              {open ? t('common-ui:show-less.info.abbr', 'Less') : t('common-ui:show-more.info.abbr', 'More')}
            </InlineButton>
          </div>
        </>
      )}
    </div>
  )
}

DmcMoreLess.propTypes = {
  /** The text component to truncate. */
  children: PropTypes.node.isRequired,

  /** The number of lines to show when the text is collapsed. */
  collapsedLineCount: PropTypes.number,

  /** Allows us to control expanding and collapsing without clicking on button */
  showMore: PropTypes.bool,

  /** Allows us to seperately pass in a text so the children could be a link */
  text: PropTypes.string,

  /** Allows us to run a function everytime more or less button is clicked */
  onMoreLessClick: PropTypes.func,

  /** Add custom props to the more/less button. */
  buttonProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
}
